import { motion } from 'framer-motion';
import { Users, Target, Lightbulb, Heart } from 'lucide-react';

const team = [
  {
    name: 'Mike Williams',
    role: 'Technical Manager',
    bio: 'Leading technical strategy and architecture decisions with 10+ years of experience in Window, Linux, MacOS, other tech and web development.',
  },
  {
    name: 'Cameron Macdonald',
    role: 'General Manager',
    bio: 'Drives operations and client success through clarity, collaboration, and consistent delivery.',
  },
];

const timeline = [
  { phase: 'Discover', description: 'Initial consultation and requirements gathering' },
  { phase: 'Plan', description: 'Architecture design and technical specification' },
  { phase: 'Build', description: 'Development and implementation' },
  { phase: 'Test', description: 'Quality assurance and performance testing' },
  { phase: 'Launch', description: 'Deployment to production' },
  { phase: 'Support', description: 'Ongoing maintenance and optimization' },
];

const values = [
  {
    icon: Target,
    title: 'Technical Excellence',
    description: 'We prioritize clean code, modern practices, and sustainable solutions.',
  },
  {
    icon: Users,
    title: 'Client Partnership',
    description: 'Your success is our success. We build long-term relationships.',
  },
  {
    icon: Lightbulb,
    title: 'Innovation',
    description: 'Staying ahead with the latest technologies and best practices.',
  },
  {
    icon: Heart,
    title: 'Quality First',
    description: 'We never compromise on quality, security, or performance.',
  },
];

export default function About() {
  return (
    <div className="min-h-screen pt-16">
      {/* Hero */}
      <section className="py-24 bg-gradient-to-br from-night-900 via-night-800 to-night-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              About <span className="bg-gradient-to-r from-launch-blue via-launch-purple to-launch-orange bg-clip-text text-transparent">Cloud Launch</span>
            </h1>
            <p className="text-xl text-offwhite/80 max-w-3xl mx-auto">
              We're a technical web agency specializing in custom development and 
              long-term technical partnerships.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-24 bg-night-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center"
            initial={{ opacity: 1, y: 0 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl font-bold mb-6">Our Mission</h2>
            <p className="text-xl text-offwhite/80 leading-relaxed">
              To empower businesses with modern, custom technology solutions that drive 
              growth and innovation. We believe in building lasting partnerships based on 
              trust, technical excellence, and shared success.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-night-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-16">Our Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                className="bg-night-700 rounded-xl p-6 text-center"
                initial={{ opacity: 1, y: 0 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <value.icon className="w-12 h-12 text-launch-blue mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">{value.title}</h3>
                <p className="text-offwhite/70">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-24 bg-night-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-16">Meet the Team</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-4xl mx-auto">
            {team.map((member, index) => (
              <motion.div
                key={member.name}
                className="bg-night-700 rounded-xl p-8 text-center"
                initial={{ opacity: 1, y: 0 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="w-32 h-32 rounded-full overflow-hidden mx-auto mb-6">
                  <img
                    src={index === 0 ? '/team/mike.png' : '/team/cameron.png'}
                    alt={`${member.name}`}
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-2xl font-semibold mb-2">{member.name}</h3>
                <p className="text-launch-blue font-medium mb-4">{member.role}</p>
                <p className="text-offwhite/70">{member.bio}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Launch Method Timeline */}
      <section className="py-24 bg-night-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">The Launch Method</h2>
            <p className="text-offwhite/70 text-lg">Our proven process for successful projects</p>
          </div>
          <div className="max-w-4xl mx-auto">
            {timeline.map((step, index) => (
              <motion.div
                key={step.phase}
                className="flex gap-6 mb-8 last:mb-0"
                initial={{ opacity: 1, x: 0 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-r from-launch-blue to-launch-purple flex items-center justify-center text-white font-bold text-lg">
                    {index + 1}
                  </div>
                </div>
                <div className="flex-1 bg-night-700 rounded-xl p-6">
                  <h3 className="text-xl font-semibold mb-2 text-launch-blue">{step.phase}</h3>
                  <p className="text-offwhite/70">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
